import React, { useState, useEffect } from "react";
import { saveAs } from "file-saver";
import {
  Box,
  Flex,
  Text,
  Button,
  Center,
  Container,
  VStack,
  Heading,
  Grid,
  Icon,
  Input,
  SimpleGrid,
  Badge,
} from "@chakra-ui/react";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "../../Configuration/apiConfig";
import {
  FiDownload,
  FiCalendar,
  FiFileText,
  FiAlertCircle,
} from "react-icons/fi";
import { BsGraphUp } from "react-icons/bs";
import { TbReportAnalytics } from "react-icons/tb";
import { set } from "date-fns";

export default function DownloadExcel({
  asmData = null,
  channelData = null,
  subregionData = null,
  bnlData = null,
  bntlData = null,
}) {
  const [loading, setLoading] = useState(false);
  
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const fromDateDefault = `${year}-${month}-01`;
  const toDateDefault = `${year}-${month}-${day}`;

  const [fromDate, setFromDate] = useState(fromDateDefault);
  const [toDate, setToDate] = useState(toDateDefault);
  
  
  const labels = [
    "Total Shops",
    "Total RED Score",
    "Cooler Score",
    "Availability",
    "Activation",
    "Cooler Bonus",
  ];
  const [recentReports, setRecentReports] = useState([]);
  // Helper function to generate past three months (excluding selected month)
  const generatePastThreeMonths = (selectedMonth) => {
    if (!selectedMonth) {
      // If no month is selected, show past three months
      const currentDate = new Date();
      const months = [];

      for (let i = 1; i <= 3; i++) {
        const date = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - i,
          1
        );
        const monthName = date.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
        const size = `${(10 + Math.random()).toFixed(1)} KB`;

        months.push({
          month: monthName,
          size: size,
          status: "completed",
          dateValue: date,
        });
      }

      return months;
    }

    // If month is selected, show three months before selected month
    const [year, month] = selectedMonth.split("-").map(Number);
    const months = [];

    for (let i = 1; i <= 3; i++) {
      const date = new Date(year, month - 1 - i, 1);
      const monthName = date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      const size = `${(100 + Math.random() * 2).toFixed(1)} KB`;

      months.push({
        month: monthName,
        size: size,
        status: "completed",
        dateValue: date,
      });
    }

    return months;
  };
  // Update recent reports when selectedMonthValue changes
  useEffect(() => {
    setRecentReports(generatePastThreeMonths(null));
  }, []);
  const handleDownloadReport = (report) => {
    console.log("Downloading report for:", report);
    const year = report.dateValue.getFullYear();
    const month = report.dateValue.getMonth() + 1;
    const firstDay = new Date(year, month - 1, 1).toISOString().split('T')[0];
    const lastDay = new Date(year, month, 0).toISOString().split('T')[0];
    console.log("Date range to set:", firstDay, "to", lastDay);
    // Set the date range and trigger download
    setFromDate(firstDay);
    setToDate(lastDay);

    // Trigger download after a small delay to ensure state is updated
    setTimeout(() => {
      handleExport();
    }, 100);
  };
  // Build workbook with ExcelJS (unchanged)
  const buildWorkbook = async (
    asmJson,
    channelJson,
    subregionJson,
    bnlJson,
    bntlJson
  ) => {
    const ExcelJSModule = await import("exceljs/dist/exceljs.min.js").catch(
      async () => await import("exceljs")
    );
    const ExcelJS = ExcelJSModule.default || ExcelJSModule;
    const wb = new ExcelJS.Workbook();

    // Common styling
    const asmHeaderFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFF8000" },
    }; // orange
    const asmHeaderFont = { bold: true, color: { argb: "FFFFFFFF" } }; // white text
    const soHeaderFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFFE6CC" },
    }; // pale

    //
    // 1) RED SCORE sheet (ASM)
    //
    const wsA = wb.addWorksheet("RED SCORE");
    for (let i = 0; i < labels.length; i++) {
      const r = 2 + i;
      const cell = wsA.getCell(r, 1);
      cell.value = labels[i];
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "center" };
    }
    const asmSummary = asmJson?.asmLevelReport || {};
    const overallAsm = [
      asmSummary.totalShops ?? "",
      asmSummary.totalRedScore ?? "",
      asmSummary.coolerScore ?? "",
      asmSummary.availability ?? "",
      asmSummary.activation ?? "",
      asmSummary.coolerBonus ?? "",
    ];
    wsA.getCell(1, 2).value = "Overall";
    wsA.getCell(1, 2).font = { bold: true };
    wsA.getCell(1, 2).alignment = {
      textRotation: 90,
      horizontal: "center",
      vertical: "bottom",
    };
    for (let i = 0; i < overallAsm.length; i++) {
      wsA.getCell(2 + i, 2).value = overallAsm[i];
      wsA.getCell(2 + i, 2).alignment = {
        horizontal: "center",
        vertical: "center",
      };
    }

    let col = 3;
    const asmList = Array.isArray(asmJson?.asmSoData) ? asmJson.asmSoData : [];
    for (const asmBlock of asmList) {
      const asm = asmBlock.asmSummary || {};
      const h = wsA.getCell(1, col);
      h.value = asm.asmName ?? "";
      h.alignment = {
        textRotation: 90,
        horizontal: "center",
        vertical: "bottom",
        wrapText: true,
      };
      h.fill = asmHeaderFill;
      h.font = asmHeaderFont;
      h.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      const asmValues = [
        asm.totalStores ?? "",
        asm.redScoreAvg ?? "",
        asm.coolerScoreAvg ?? "",
        asm.availabilityScoreAvg ?? "",
        asm.activationScoreAvg ?? "",
        asm.coolerBonusAvg ?? "",
      ];
      for (let i = 0; i < asmValues.length; i++) {
        const r = 2 + i;
        const cell = wsA.getCell(r, col);
        cell.value = asmValues[i];
        cell.alignment = { horizontal: "center", vertical: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }
      wsA.getColumn(col).width = 6;
      col += 1;

      const soList = Array.isArray(asmBlock.soDetails)
        ? asmBlock.soDetails
        : [];
      for (const so of soList) {
        const sh = wsA.getCell(1, col);
        sh.value = so.soName ?? "";
        sh.alignment = {
          textRotation: 90,
          horizontal: "center",
          vertical: "bottom",
          wrapText: true,
        };
        sh.fill = soHeaderFill;
        sh.font = { bold: false, color: { argb: "FF000000" } };
        sh.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        const soValues = [
          so.totalStores ?? "",
          so.redScoreAvg ?? "",
          so.coolerScoreAvg ?? "",
          so.availabilityScoreAvg ?? "",
          so.activationScoreAvg ?? "",
          so.coolerBonusAvg ?? "",
        ];
        for (let i = 0; i < soValues.length; i++) {
          const r = 2 + i;
          const cell = wsA.getCell(r, col);
          cell.value = soValues[i];
          cell.alignment = { horizontal: "center", vertical: "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }
        wsA.getColumn(col).width = 6;
        col += 1;
      }
    }
    // make headers taller
    wsA.getRow(1).height = 120;
    wsA.getColumn(1).width = 24;
    wsA.getColumn(2).width = 12;
    wsA.views = [{ state: "frozen", ySplit: 1 }];

    //
    // 2) CHANNELS sheet
    //
    const wsC = wb.addWorksheet("CHANNELS");
    for (let i = 0; i < labels.length; i++) {
      const r = 2 + i;
      const cell = wsC.getCell(r, 1);
      cell.value = labels[i];
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "center" };
    }
    const channelSummary = channelJson?.summary || {};
    const overallChan = [
      channelSummary.totalShops ?? "",
      channelSummary.totalRedScore ?? "",
      channelSummary.coolerScore ?? "",
      channelSummary.availability ?? "",
      channelSummary.activation ?? "",
      channelSummary.coolerBonus ?? "",
    ];
    wsC.getCell(1, 2).value = "Overall";
    wsC.getCell(1, 2).font = { bold: true };
    wsC.getCell(1, 2).alignment = {
      textRotation: 90,
      horizontal: "center",
      vertical: "bottom",
    };
    for (let i = 0; i < overallChan.length; i++) {
      wsC.getCell(2 + i, 2).value = overallChan[i];
      wsC.getCell(2 + i, 2).alignment = {
        horizontal: "center",
        vertical: "center",
      };
    }

    let ccol = 3;
    const channels = Array.isArray(channelJson?.channels)
      ? channelJson.channels
      : [];
    for (const ch of channels) {
      const name = ch.channelName ?? "";
      const vals = ch.channels ?? {};

      const chHdr = wsC.getCell(1, ccol);
      chHdr.value = name;
      chHdr.alignment = {
        textRotation: 90,
        horizontal: "center",
        vertical: "bottom",
        wrapText: true,
      };
      chHdr.fill = asmHeaderFill;
      chHdr.font = asmHeaderFont;
      chHdr.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      const chValues = [
        vals.totalShops ?? "",
        vals.totalRedScore ?? "",
        vals.coolerScore ?? "",
        vals.availability ?? "",
        vals.activation ?? "",
        vals.coolerBonus ?? "",
      ];
      for (let i = 0; i < chValues.length; i++) {
        const r = 2 + i;
        const cell = wsC.getCell(r, ccol);
        cell.value = chValues[i];
        cell.alignment = { horizontal: "center", vertical: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }
      wsC.getColumn(ccol).width = 9;
      ccol += 1;
    }

    wsC.getRow(1).height = 60;
    wsC.getColumn(1).width = 24;
    wsC.getColumn(2).width = 12;
    wsC.views = [{ state: "frozen", ySplit: 1 }];

    //
    // 3) REGIONWISE SCORE sheet (Subregions)
    //
    const wsS = wb.addWorksheet("REGIONWISE SCORE");
    for (let i = 0; i < labels.length; i++) {
      const r = 2 + i;
      const cell = wsS.getCell(r, 1);
      cell.value = labels[i];
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "center" };
    }
    const subSummary = subregionJson?.summary || {};
    const overallSub = [
      subSummary.totalShops ?? "",
      subSummary.totalRedScore ?? "",
      subSummary.coolerScore ?? "",
      subSummary.availability ?? "",
      subSummary.activation ?? "",
      subSummary.coolerBonus ?? "",
    ];
    wsS.getCell(1, 2).value = "Overall";
    wsS.getCell(1, 2).font = { bold: true };
    wsS.getCell(1, 2).alignment = {
      textRotation: 90,
      horizontal: "center",
      vertical: "bottom",
    };
    for (let i = 0; i < overallSub.length; i++) {
      wsS.getCell(2 + i, 2).value = overallSub[i];
      wsS.getCell(2 + i, 2).alignment = {
        horizontal: "center",
        vertical: "center",
      };
    }

    let scol = 3;
    const subList = Array.isArray(subregionJson?.subregions)
      ? subregionJson.subregions
      : [];
    for (const sub of subList) {
      const name = sub.subRegionName ?? "";
      const vals = sub.summary ?? {};

      const sh = wsS.getCell(1, scol);
      sh.value = name;
      sh.alignment = {
        textRotation: 90,
        horizontal: "center",
        vertical: "bottom",
        wrapText: true,
      };
      sh.fill = asmHeaderFill;
      sh.font = asmHeaderFont;
      sh.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      const sVals = [
        vals.totalShops ?? "",
        vals.totalRedScore ?? "",
        vals.coolerScore ?? "",
        vals.availability ?? "",
        vals.activation ?? "",
        vals.coolerBonus ?? "",
      ];
      for (let i = 0; i < sVals.length; i++) {
        const r = 2 + i;
        const cell = wsS.getCell(r, scol);
        cell.value = sVals[i];
        cell.alignment = { horizontal: "center", vertical: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }
      wsS.getColumn(scol).width = 9;
      scol += 1;
    }

    wsS.getRow(1).height = 60;
    wsS.getColumn(1).width = 24;
    wsS.getColumn(2).width = 12;
    wsS.views = [{ state: "frozen", ySplit: 1 }];

    //
    // 4) BNL sheet
    //
    const wsB = wb.addWorksheet("BNL");
    for (let i = 0; i < labels.length; i++) {
      const r = 2 + i;
      const cell = wsB.getCell(r, 1);
      cell.value = labels[i];
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "center" };
    }
    const bnlSummary = bnlJson?.totalSummary || {};
    const overallBnl = [
      bnlSummary.totalShops ?? "",
      bnlSummary.totalRedScore ?? "",
      bnlSummary.coolerScore ?? "",
      bnlSummary.availability ?? "",
      bnlSummary.activation ?? "",
      bnlSummary.coolerBonus ?? "",
    ];
    wsB.getCell(1, 2).value = "Overall";
    wsB.getCell(1, 2).font = { bold: true };
    wsB.getCell(1, 2).alignment = {
      textRotation: 90,
      horizontal: "center",
      vertical: "bottom",
    };
    for (let i = 0; i < overallBnl.length; i++) {
      wsB.getCell(2 + i, 2).value = overallBnl[i];
      wsB.getCell(2 + i, 2).alignment = {
        horizontal: "center",
        vertical: "center",
      };
    }

    let bcol = 3;
    const mdcList = Array.isArray(bnlJson?.mdcData) ? bnlJson.mdcData : [];
    for (const mdcBlock of mdcList) {
      const mdc = mdcBlock.asmSummary || {};
      const h = wsB.getCell(1, bcol);
      h.value = mdcBlock.distributorName ?? "";
      h.alignment = {
        textRotation: 90,
        horizontal: "center",
        vertical: "bottom",
        wrapText: true,
      };
      h.fill = asmHeaderFill;
      h.font = asmHeaderFont;
      h.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      const mdcValues = [
        mdc.totalShops ?? "",
        mdc.totalRedScore ?? "",
        mdc.coolerScore ?? "",
        mdc.availability ?? "",
        mdc.activation ?? "",
        mdc.coolerBonus ?? "",
      ];
      for (let i = 0; i < mdcValues.length; i++) {
        const r = 2 + i;
        const cell = wsB.getCell(r, bcol);
        cell.value = mdcValues[i];
        cell.alignment = { horizontal: "center", vertical: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }
      wsB.getColumn(bcol).width = 15;
      bcol += 1;

      const soList = Array.isArray(mdcBlock.soDetails)
        ? mdcBlock.soDetails
        : [];
      for (const so of soList) {
        const sh = wsB.getCell(1, bcol);
        sh.value = so.soName ?? "";
        sh.alignment = {
          textRotation: 90,
          horizontal: "center",
          vertical: "bottom",
          wrapText: true,
        };
        sh.fill = soHeaderFill;
        sh.font = { bold: false, color: { argb: "FF000000" } };
        sh.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        const soValues = [
          so.totalStores ?? "",
          so.redScoreAvg ?? "",
          so.coolerScoreAvg ?? "",
          so.availabilityScoreAvg ?? "",
          so.activationScoreAvg ?? "",
          so.coolerBonusAvg ?? "",
        ];
        for (let i = 0; i < soValues.length; i++) {
          const r = 2 + i;
          const cell = wsB.getCell(r, bcol);
          cell.value = soValues[i];
          cell.alignment = { horizontal: "center", vertical: "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }
        wsB.getColumn(bcol).width = 15;
        bcol += 1;
      }
    }
    // make headers taller
    wsB.getRow(1).height = 120;
    wsB.getColumn(1).width = 24;
    wsB.getColumn(2).width = 12;
    wsB.views = [{ state: "frozen", ySplit: 1 }];

    //
    // 5) BNTL sheet
    //
    const wsT = wb.addWorksheet("BNTL");
    for (let i = 0; i < labels.length; i++) {
      const r = 2 + i;
      const cell = wsT.getCell(r, 1);
      cell.value = labels[i];
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left", vertical: "center" };
    }
    const bntlSummary = bntlJson?.totalSummary || {};
    const overallBntl = [
      bntlSummary.totalShops ?? "",
      bntlSummary.totalRedScore ?? "",
      bntlSummary.coolerScore ?? "",
      bntlSummary.availability ?? "",
      bntlSummary.activation ?? "",
      bntlSummary.coolerBonus ?? "",
    ];
    wsT.getCell(1, 2).value = "Overall";
    wsT.getCell(1, 2).font = { bold: true };
    wsT.getCell(1, 2).alignment = {
      textRotation: 90,
      horizontal: "center",
      vertical: "bottom",
    };
    for (let i = 0; i < overallBntl.length; i++) {
      wsT.getCell(2 + i, 2).value = overallBntl[i];
      wsT.getCell(2 + i, 2).alignment = {
        horizontal: "center",
        vertical: "center",
      };
    }

    let tcol = 3;
    const mdcListT = Array.isArray(bntlJson?.mdcData) ? bntlJson.mdcData : [];
    for (const mdcBlock of mdcListT) {
      const mdc = mdcBlock.asmSummary || {};
      const h = wsT.getCell(1, tcol);
      h.value = mdcBlock.distributorName ?? "";
      h.alignment = {
        textRotation: 90,
        horizontal: "center",
        vertical: "bottom",
        wrapText: true,
      };
      h.fill = asmHeaderFill;
      h.font = asmHeaderFont;
      h.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      const mdcValues = [
        mdc.totalShops ?? "",
        mdc.totalRedScore ?? "",
        mdc.coolerScore ?? "",
        mdc.availability ?? "",
        mdc.activation ?? "",
        mdc.coolerBonus ?? "",
      ];
      for (let i = 0; i < mdcValues.length; i++) {
        const r = 2 + i;
        const cell = wsT.getCell(r, tcol);
        cell.value = mdcValues[i];
        cell.alignment = { horizontal: "center", vertical: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }
      wsT.getColumn(tcol).width = 15;
      tcol += 1;

      const soList = Array.isArray(mdcBlock.soDetails)
        ? mdcBlock.soDetails
        : [];
      for (const so of soList) {
        const sh = wsT.getCell(1, tcol);
        sh.value = so.soName ?? "";
        sh.alignment = {
          textRotation: 90,
          horizontal: "center",
          vertical: "bottom",
          wrapText: true,
        };
        sh.fill = soHeaderFill;
        sh.font = { bold: false, color: { argb: "FF000000" } };
        sh.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        const soValues = [
          so.totalStores ?? "",
          so.redScoreAvg ?? "",
          so.coolerScoreAvg ?? "",
          so.availabilityScoreAvg ?? "",
          so.activationScoreAvg ?? "",
          so.coolerBonusAvg ?? "",
        ];
        for (let i = 0; i < soValues.length; i++) {
          const r = 2 + i;
          const cell = wsT.getCell(r, tcol);
          cell.value = soValues[i];
          cell.alignment = { horizontal: "center", vertical: "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }
        wsT.getColumn(tcol).width = 15;
        tcol += 1;
      }
    }
    // make headers taller
    wsT.getRow(1).height = 120;
    wsT.getColumn(1).width = 24;
    wsT.getColumn(2).width = 12;
    wsT.views = [{ state: "frozen", ySplit: 1 }];

    return wb;
  };
  const handleExport = async () => {
    setLoading(true);

    const body = {
      from: {
        date: parseInt(fromDate.split('-')[2]),
        month: parseInt(fromDate.split('-')[1]),
        year: parseInt(fromDate.split('-')[0])
      },
      to: {
        date: parseInt(toDate.split('-')[2]),
        month: parseInt(toDate.split('-')[1]),
        year: parseInt(toDate.split('-')[0])
      }
    };

    try {
      let asmJson = asmData;
      if (!asmJson) {
        const resAsm = await apiClient.post(`${API_CONFIG.BASE_URL}/red-score/api/report`, body);
        if (resAsm.status !== 200)
          throw new Error("ASM API fetch failed: " + resAsm.status);
        asmJson = resAsm.data;
      }

      let channelJson = channelData;
      if (!channelJson) {
        const resChan = await apiClient.post(`${API_CONFIG.BASE_URL}/channel-level-report`, body);
        if (resChan.status !== 200)
          throw new Error("Channel API fetch failed: " + resChan.status);
        channelJson = resChan.data;
      }

      let subJson = subregionData;
      if (!subJson) {
        const resSub = await apiClient.post(`${API_CONFIG.BASE_URL}/api/subregion-report`, body);
        if (resSub.status !== 200)
          throw new Error("Subregion API fetch failed: " + resSub.status);
        subJson = resSub.data;
      }

      let bnlJson = bnlData;
      if (!bnlJson) {
        const resBnl = await apiClient.post(`${API_CONFIG.BASE_URL}/api/report/bnl`, body);
        if (resBnl.status !== 200)
          throw new Error("BNL API fetch failed: " + resBnl.status);
        bnlJson = resBnl.data;
      }

      let bntlJson = bntlData;
      if (!bntlJson) {
        const resBntl = await apiClient.post(`${API_CONFIG.BASE_URL}/api/report/bntl`, body);
        if (resBntl.status !== 200)
          throw new Error("BNTL API fetch failed: " + resBntl.status);
        bntlJson = resBntl.data;
      }

      console.log(
        "Building workbook with ASM, Channel, Subregion, BNL and BNTL data..."
      );
      const wb = await buildWorkbook(
        asmJson,
        channelJson,
        subJson,
        bnlJson,
        bntlJson
      );
      const buffer = await wb.xlsx.writeBuffer();
      const fromParts = fromDate.split('-');
      const toParts = toDate.split('-');
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        `combined_report_${fromDate.replace(/-/g, '')}_to_${toDate.replace(/-/g, '')}.xlsx`
      );
    } catch (err) {
      console.error("Export error:", err);
      alert("Failed to generate Excel: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Container maxW="container.xl" p="0" m="0">
        <Box bg="gray.50" overflow="hidden" mt="4rem">
          {/* Header Section */}
          <Box bg="white" borderRadius="2xl" mb={8} pt={5}>
            <VStack align="flex-start" spacing={2}>
              <Flex align="center" gap={3}>
                <Icon as={TbReportAnalytics} boxSize={8} color="red.600" />
                <Heading size="lg" color="gray.800">
                  Analytics Reports Dashboard
                </Heading>
              </Flex>
              <Text color="gray.500">
                Generate comprehensive Excel reports for RED Score, Channels,
                Regionwise Score, BNL and BNTL metrics. Select your desired
                date range to download complete analytics.
              </Text>
            </VStack>
          </Box>

          {/* Main Content Grid */}
          <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={4} mb={8}>
            {/* Left Column - Report Configuration */}
            <Box
              bg="white"
              borderRadius="2xl"
              p={{ base: 6, md: 6 }}
              boxShadow="xl"
              border="1px solid"
              borderColor="gray.200"
            >
              <Flex align="center" mb={6} gap={3}>
                <Icon as={FiCalendar} boxSize={6} color="red.600" />
                <Heading size="md" color="gray.800">
                  Report Configuration
                </Heading>
              </Flex>

              <VStack spacing={8} align="stretch">
                {/* Date Range Selection Card */}
                <Box
                  bg="red.50"
                  borderRadius="xl"
                  p={6}
                  border="2px solid"
                  borderColor="gray.200"
                  transition="all 0.3s"
                  _hover={{ borderColor: "red.300", bg: "red.50" }}
                >
                  <VStack align="stretch" spacing={4}>
                    <Flex align="center" gap={2}>
                      <Icon as={FiCalendar} color="red.600" />
                      <Text fontWeight="bold" color="gray.800">
                        Select Reporting Period
                      </Text>
                    </Flex>

                    <Flex direction={{ base: "column", md: "row" }} spacing={4} align="stretch">
                      <Box flex={1}>
                        <Text
                          fontSize="sm"
                          color="gray.600"
                          mb={2}
                          fontWeight="medium"
                        >
                          From Date
                        </Text>
                        <Box position="relative">
                          <Icon
                            as={FiCalendar}
                            position="absolute"
                            left="4"
                            top="50%"
                            transform="translateY(-50%)"
                            color="red.500"
                            zIndex={1}
                          />
                          <Input
                            type="date"
                            value={fromDate}
                            onChange={(e) =>
                              setFromDate(e.target.value)
                            }
                            size="lg"
                            w="80"
                            pl="12"
                            borderRadius="lg"
                            border="2px solid"
                            borderColor="gray.300"
                            bg="white"
                            fontSize="md"
                            fontWeight="medium"
                            _hover={{ borderColor: "red.400" }}
                            _focus={{
                              borderColor: "red.500",
                              boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.15)",
                            }}
                          />
                        </Box>
                      </Box>
                      <Box flex={1}>
                        <Text
                          fontSize="sm"
                          color="gray.600"
                          mb={2}
                          fontWeight="medium"
                        >
                          To Date
                        </Text>
                        <Box position="relative">
                          <Icon
                            as={FiCalendar}
                            position="absolute"
                            left="4"
                            top="50%"
                            transform="translateY(-50%)"
                            color="red.500"
                            zIndex={1}
                          />
                          <Input
                            type="date"
                            value={toDate}
                            onChange={(e) =>
                              setToDate(e.target.value)
                            }
                            min={fromDate}
                            size="lg"
                            w="80"
                            pl="12"
                            borderRadius="lg"
                            border="2px solid"
                            borderColor="gray.300"
                            bg="white"
                            fontSize="md"
                            fontWeight="medium"
                            _hover={{ borderColor: "red.400" }}
                            _focus={{
                              borderColor: "red.500",
                              boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.15)",
                            }}
                          />
                        </Box>
                      </Box>
                    </Flex>
                  </VStack>
                </Box>

                {/* Report Types Card */}
                <Box
                  bg="red.50"
                  borderRadius="xl"
                  p={6}
                  border="2px dashed"
                  borderColor="gray.300"
                >
                  <VStack align="stretch" spacing={4}>
                    <Flex align="center" gap={2}>
                      <Icon as={FiFileText} color="red.600" />
                      <Text fontWeight="bold" color="gray.800">
                        Included Reports
                      </Text>
                    </Flex>

                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                      {[
                        { name: "RED Score", color: "green" },
                        { name: "Channels", color: "red" },
                        { name: "Regionwise Score", color: "purple" },
                        { name: "BNL", color: "orange" },
                        { name: "BNTL", color: "red" },
                      ].map((report, index) => (
                        <Flex
                          key={index}
                          align="center"
                          gap={2}
                          bg="white"
                          p={3}
                          borderRadius="lg"
                          border="1px solid"
                          borderColor="gray.200"
                        >
                          <Box
                            w="3"
                            h="3"
                            borderRadius="full"
                            bg={`${report.color}.500`}
                          />
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.700"
                          >
                            {report.name}
                          </Text>
                        </Flex>
                      ))}
                    </SimpleGrid>
                  </VStack>
                </Box>
              </VStack>
            </Box>

            {/* Right Column - Download Panel */}
            <Box
              bgGradient="linear(135deg, #C41E3A 0%, #F40009 50%, #DC2626 100%)"
              borderRadius="2xl"
              p={{ base: 6, md: 6 }}
              boxShadow="0 20px 60px rgba(244, 0, 9, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1) inset"
              color="white"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              position="relative"
              overflow="hidden"
              _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "50%",
                background: "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)",
                pointerEvents: "none",
              }}
              sx={{
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
              }}
            >
              <VStack spacing={8} align="center" textAlign="center" position="relative" zIndex={1}>
                <Icon
                  as={BsGraphUp}
                  boxSize={16}
                  opacity="0.95"
                  filter="drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))"
                />

                <Box>
                  <Heading
                    size="lg"
                    mb={3}
                    fontWeight="800"
                    letterSpacing="0.02em"
                    textShadow="0 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(255, 255, 255, 0.1)"
                  >
                    Ready to Download
                  </Heading>
                  <Text
                    fontSize="lg"
                    fontWeight="600"
                    opacity="0.95"
                    textShadow="0 1px 4px rgba(0, 0, 0, 0.2)"
                    lineHeight="1.6"
                  >
                    Generate your <strong style={{ fontWeight: "700", color: "#FFE5E5" }}>comprehensive analytics report</strong> with all
                    metrics included
                  </Text>
                </Box>

                <Button
                  onClick={handleExport}
                  isLoading={loading}
                  loadingText="Generating Report..."
                  size="lg"
                  bg="white"
                  color="red.600"
                  leftIcon={<FiDownload />}
                  px={12}
                  py={6}
                  fontWeight="bold"
                  fontSize="lg"
                  borderRadius="xl"
                  boxShadow="0 10px 30px rgba(244, 0, 9, 0.3), 0 0 0 1px rgba(244, 0, 9, 0.1) inset"
                  _hover={{
                    transform: "translateY(-3px)",
                    boxShadow: "0 15px 40px rgba(244, 0, 9, 0.4), 0 0 0 1px rgba(244, 0, 9, 0.2) inset",
                    bg: "red.50",
                    color: "red.700",
                  }}
                  _active={{
                    transform: "translateY(-1px)",
                  }}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  width="100%"
                >
                  Download Excel Report
                </Button>

                {/* <VStack spacing={2} fontSize="sm" opacity="0.8">
                  <Flex align="center" gap={2}>
                    <Icon as={FiFileText} />
                    <Text>Single consolidated Excel file</Text>
                  </Flex>
                  <Flex align="center" gap={2}>
                    <Icon as={FiAlertCircle} />
                    <Text>Includes all selected metrics</Text>
                  </Flex>
                </VStack> */}
              </VStack>
            </Box>
          </Grid>

          {/* Recent Reports Section */}
          <Box
            bg="white"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            boxShadow="xl"
            border="1px solid"
            borderColor="gray.200"
          >
            <Flex align="center" justify="space-between" mb={6}>
              <Flex align="center" gap={3}>
                <Icon as={FiDownload} boxSize={6} color="red.600" />
                <Heading size="md" color="gray.800">
                  Recent Reports
                </Heading>
              </Flex>
              <Badge
                colorScheme="red"
                variant="subtle"
                px={4}
                py={1}
                borderRadius="full"
              >
                Past 3 months
              </Badge>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {recentReports.map((report, index) => (
                <Box
                  key={index}
                  bg="gray.50"
                  borderRadius="lg"
                  p={4}
                  border="1px solid"
                  borderColor="gray.200"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: "red.300",
                    transform: "translateY(-2px)",
                    boxShadow: "md",
                  }}
                >
                  <Flex justify="space-between" align="start" mb={3}>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold" color="gray.800">
                        {report.month}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {report.size}
                      </Text>
                    </VStack>
                    <Badge colorScheme="green" variant="subtle">
                      {report.status}
                    </Badge>
                  </Flex>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    width="100%"
                    leftIcon={<FiDownload />}
                    onClick={() => handleDownloadReport(report)}
                  >
                    Download Again
                  </Button>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          {/* Footer Note */}
          <Box mt={8} textAlign="center">
            <Text fontSize="sm" color="gray.500" maxW="2xl" mx="auto">
              Reports are generated in real-time and may take a few moments to
              process depending on the selected date range's data volume.
            </Text>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}